<template>
  <div
    class="selectContainer"
    :class="[selectContainerClass, { 'pointer-events-none': disabled }]"
    ref="container"
  >
    <div
      v-if="label"
      :class="['label', labelClass, { '!text-[#b7b2b5]': disabled }]"
    >
      {{ label }}
    </div>

    <div :id="input_id" class="relative">
      <div
        class="inputCon"
        :class="[
          customClass,
          { disabled: disabled, 'border-active': openOption },
        ]"
        @click="onTriggerClick"
      >
        <input
          v-if="searchable"
          ref="inlineInput"
          v-model="searchText"
          type="text"
          :placeholder="placeholder"
          class="inputText truncate outline-none bg-transparent border-none w-full"
          :class="{
            'text-[#1F1D1F]':
              searchText ||
              hasValueOrZero ||
              (defaultVal !== undefined && modelValueLooseEq(defaultVal)),
            'text-[#B7B2B5]': !(
              searchText ||
              hasValueOrZero ||
              (defaultVal !== undefined && modelValueLooseEq(defaultVal))
            ),
          }"
          :disabled="disabled"
          @focus="openDropdown"
          @keydown.stop="onKeydown"
        />

        <div
          v-else
          class="inputText truncate"
          :class="hasValueOrZero ? 'text-[#1F1D1F]' : 'text-[#B7B2B5]'"
        >
          {{ (hasValueOrZero && nameValue) || placeholder }}
        </div>

        <div>
          <button
            v-if="showClearButton"
            type="button"
            class="absolute end-4 top-1/2 -translate-y-1/2 p-[2px] rounded"
            @click.stop="clearSelection"
            aria-label="Clear selection"
            title="ล้างค่า"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
            >
              <path
                d="M6 6l8 8M14 6l-8 8"
                stroke="#555055"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <div
            v-else
            class="absolute end-4 top-1/2 -translate-y-1/2 duration-200"
            :class="{ 'rotate-180': openOption, 'text-[#B7B2B5]': disabled }"
          >
            <CaretDownIcon />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="alertWarningMessage && warningMessage"
      :class="['textError', { 'mt-[8px]': warningMessage }]"
    >
      {{ warningMessage }}
    </div>

    <teleport to="body">
      <div
        v-show="openOption"
        class="dropdownCon"
        :style="dropdownPosition"
        ref="dropdown"
        @click.stop
      >
        <template v-if="filteredOptions.length > 0">
          <div
            v-for="(item, index) in filteredOptions"
            :key="optionKey(item, index)"
            class="box"
            :class="{
              hidden:
                (!optionView && item.optionView === false) ||
                hideValues.includes(valueFromItem(item)),
              selected: isSelected(item),
              ' ': index === activeIndex,
            }"
            @mouseenter="activeIndex = index"
            @click.stop="selectOption(item)"
          >
            {{ labelFromItem(item) }}
          </div>
        </template>

        <div
          v-else
          class="box !text-[#B7B2B5] cursor-default"
          @click.stop="closeDropdown"
        >
          ไม่พบข้อมูล
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from "vue";
import provinceData from "@/assets/json/provinces.json";
import districtData from "@/assets/json/districts.json";
import subDistrictData from "@/assets/json/subdistricts.json";


const props = defineProps({
  selectContainerClass: String,
  label: String,
  labelClass: String,
  inputId: { type: String, default: "select" },
  options: { type: Array, default: () => [] },

  keyValue: { type: String, default: "" },
  value: { type: [String, Number, Object], default: "" },
  modelValue: { type: [String, Number, Object], default: undefined },
  placeholder: { type: String, default: "" },
  keyName: { type: String, default: "" },

  required: Boolean,
  warningMessage: { type: String, default: "" },
  disabled: Boolean,
  isCheckEmptyObject: { type: Boolean, default: false },
  lengthOptionList: { type: Number, default: 5 },
  customClass: { type: String, default: "" },
  allowEmpty: { type: Boolean, default: false },
  optionView: { type: Boolean, default: true },
  defaultVal: { type: [String, Number], default: undefined },

  // โหมด province/district/subdistrict อัตโนมัติ
  provinceCode: { type: [String, Number], default: undefined },
  districtCode: { type: [String, Number], default: undefined },

  hideValues: { type: Array, default: () => [] },
  allData: { type: Boolean, default: false },

  // ใหม่: inline search
  searchable: { type: Boolean, default: false },
  // ตัวเลือก: case insensitive (ค่าเริ่มต้น true)
  caseInsensitive: { type: Boolean, default: true },
});

const emit = defineEmits(["update:modelValue"]);

const input_id = `select_${props.inputId}`;
const openOption = ref(false);
const alertWarningMessage = ref(false);
const dropdownPosition = ref({});
const dropdown = ref(null);
const container = ref(null);

// *** เพิ่มสถานะนี้เพื่อควบคุมการ Emit ในช่วงโหลด ***
const isMounted = ref(false); 

// ค่า model ภายใน
const valInput = ref(resolveInitialValue());

// inline input (search)
const searchText = ref("");
const inlineInput = ref(null);

// รายการ focus index สำหรับคีย์บอร์ด
const activeIndex = ref(-1);

// *** เปลี่ยน watch valInput เพื่อเช็ค isMounted ก่อน Emit ***
watch(valInput, () => {
  openOption.value = false;
  checkEmptyField();
  
  // ป้องกันการ Emit ในช่วงโหลดค่าเริ่มต้น
  if (!isMounted.value) {
    return;
  }

  // ทำการ Emit เมื่อมีการเปลี่ยนแปลงค่าหลังจากโหลดเสร็จแล้ว
  if (props.allData) {
    const fullObject = props.keyValue
      ? optionsList.value.find(
          (item) => `${item[props.keyValue]}` === `${valInput.value}`
        )
      : valInput.value;

    emit("update:modelValue", fullObject || null);
  } else {
    emit("update:modelValue", valInput.value);
  }
});

function resolveInitialValue() {
  return props.modelValue !== undefined ? props.modelValue : props.value;
}

// ==== Options source (อัตโนมัติหรือ props.options) ====
const optionsList = computed(() => {
  if (props.inputId?.includes("provinceSelector")) {
    return provinceData || [];
  } else if (props.inputId?.includes("districtSelector")) {
    return (districtData || []).filter(
      (d) => `${d.provinceCode}` === `${props.provinceCode}`
    );
  } else if (props.inputId?.includes("subDistrictSelector")) {
    return (subDistrictData || []).filter(
      (s) => `${s.districtCode}` === `${props.districtCode}`
    );
  }
  return props.options || [];
});

// ==== Helpers to get value/label from item ====
function valueFromItem(item) {
  return props.keyValue ? item?.[props.keyValue] : item;
}
function labelFromItem(item) {
  if (props.keyName) return item?.[props.keyName] ?? "";
  return typeof item === "string" ? item : item?.label ?? "";
}

// label ที่แสดงเมื่อมีค่าที่เลือก (non-searchable mode)
const nameValue = computed(() => {
  const v = valInput.value;
  if (v === "" || v === null || v === undefined) return "";
  const found = optionsList.value.find((el) =>
    props.keyValue
      ? `${el?.[props.keyValue]}` == `${v}`
      : JSON.stringify(el) === JSON.stringify(v)
  );
  return labelFromItem(found) || "";
});

// มีค่าไหม (รวมกรณี '0')
const hasValueOrZero = computed(() => {
  const v = valInput.value;
  if (v === "" || v === null || v === undefined) return false;
  if (`${v}` === "0") return true;
  return true;
});

// ==== Filtering ====
const filteredOptions = computed(() => {
  const base = optionsList.value || [];
  const q = (props.searchable ? searchText.value : "").trim();

  if (!props.searchable || q === "") {
    // ซ่อนรายการที่ hideValues ในชั้น template แล้ว
    return base;
  }

  const norm = (s) =>
    props.caseInsensitive ? String(s ?? "").toLowerCase() : String(s ?? "");

  const nq = norm(q);
  return base.filter((option) => {
    const label = labelFromItem(option);
    return norm(label).includes(nq);
  });
});

// เมื่อเปิด dropdown
function openDropdown() {
  if (props.disabled) return;
  openOption.value = true;
  nextTick(() => {
    updateDropdownPosition();
    // sync searchText กับ label ปัจจุบันในครั้งแรก (ถ้าชอบ)
    if (props.searchable && inlineInput.value) {
      // ถ้ายังไม่มีข้อความ ให้โชว์ชื่อที่เลือกไว้ เพื่อแก้ไขต่อ
      if (!searchText.value && hasValueOrZero.value) {
        searchText.value = nameValue.value;
        inlineInput.value.setSelectionRange(0, searchText.value.length);
      }
    }
    // active index เริ่มที่ตัวแรก
    activeIndex.value = filteredOptions.value.length ? 0 : -1;
  });
}

// toggle จากคลิก container
function onTriggerClick() {
  if (props.disabled) return;
  if (!openOption.value) {
    openDropdown();
    inlineInput.value?.focus();
  }
}

// ปิด dropdown
function closeDropdown() {
  openOption.value = false;
  activeIndex.value = -1;
}

// คีย์บอร์ดใน input (searchable)
function onKeydown(e) {
  const len = filteredOptions.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    if (!openOption.value) openDropdown();
    if (len > 0) activeIndex.value = (activeIndex.value + 1) % len;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    if (!openOption.value) openDropdown();
    if (len > 0) activeIndex.value = (activeIndex.value - 1 + len) % len;
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (!openOption.value) return;
    const item = filteredOptions.value[activeIndex.value];
    if (item) selectOption(item);
  } else if (e.key === "Escape") {
    e.preventDefault();
    closeDropdown();
  }
}

// เลือก option
function selectOption(item) {
  const newVal = valueFromItem(item);
  valInput.value = newVal;
  // อัปเดตข้อความในช่องให้เป็น label ที่เลือก (searchable)
  if (props.searchable) {
    searchText.value = labelFromItem(item);
  }
  closeDropdown();
}

// เทียบค่าแบบ “loose” (string/number)
function modelValueLooseEq(v) {
  return `${v}` === `${valInput.value}`;
}

function isSelected(item) {
  const v = valueFromItem(item);
  return `${v}` === `${valInput.value}`;
}

function optionKey(item, idx) {
  if (typeof item === "object") {
    if (props.keyValue && item[props.keyValue] !== undefined)
      return `k-${item[props.keyValue]}`;
    if ("id" in item) return `id-${item.id}`;
  }
  return `i-${idx}`;
}

// ล้างค่า
function clearSelection() {
  valInput.value = "";
  if (props.searchable) {
    searchText.value = "";
    inlineInput.value?.focus();
    openDropdown();
  }
}

// Validate / warning
function checkEmptyField() {
  if (!props.isCheckEmptyObject) {
    alertWarningMessage.value = valInput.value === "";
  } else {
    alertWarningMessage.value = Object.values(valInput.value || {}).some((v) =>
      [null, undefined, ""].includes(v)
    );
  }
}

// Click outside -> close
function handleClickOutside(event) {
  const trigger = container.value;
  const drop = dropdown.value;
  if (
    trigger &&
    !trigger.contains(event.target) &&
    drop &&
    !drop.contains(event.target)
  ) {
    closeDropdown();
  }
}

// ตำแหน่ง dropdown
function updateDropdownPosition() {
  const triggerEl = document.getElementById(input_id);
  if (triggerEl) {
    const rect = triggerEl.getBoundingClientRect();
    dropdownPosition.value = {
      position: "absolute",
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      maxHeight: `calc(16px + (${props.lengthOptionList} * 40px))`,
      zIndex: 9999,
      background: "#fff",
      border: "1px solid #b7b2b5",
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    };
  }
}

const showClearButton = computed(() => {
  return props.allowEmpty && !props.disabled && hasValueOrZero.value;
});

// ===== Watchers sync =====
// watch นี้ยังคงใช้ immediate: true เพื่อตั้งค่าเริ่มต้น valInput.value
watch(
  () => [props.modelValue, props.value],
  () => {
    const incoming =
      props.modelValue !== undefined ? props.modelValue : props.value;
    valInput.value = incoming;

    // ตรวจสอบว่าค่าที่เข้ามาเป็นค่าว่างหรือไม่
    const isIncomingEmpty =
      incoming === "" || incoming === null || incoming === undefined;

    if (props.searchable) {
      if (isIncomingEmpty) {
        // ถ้ารีเซ็ตค่าจริง ให้รีเซ็ต searchText ด้วย
        searchText.value = "";
      } else if (!searchText.value) {
        // ถ้าค่าไม่ว่าง แต่ searchText ยังว่าง (เช่น โหลดหน้ามาครั้งแรก) ให้แสดง label ที่เลือกไว้
        searchText.value = nameValue.value;
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.warningMessage,
  () => (alertWarningMessage.value = true)
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  // init search text
  if (props.searchable && !searchText.value) {
    searchText.value = nameValue.value;
  }
  // *** ตั้งค่า isMounted เป็น true เมื่อโหลดเสร็จแล้ว
  nextTick(() => {
    isMounted.value = true;
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped lang="scss">
// ... (ส่วน style scoped ไม่ได้เปลี่ยนแปลง)
.selectContainer {
  cursor: pointer;
  width: 100%;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .label {
    color: #1f1d1f;
    font-family: Kanit;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .inputCon {
    position: relative;
    display: flex;
    max-height: 42px;
    padding: 10px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    border: 1px solid #b7b2b5;
    background: #fff;

    .inputText {
      width: calc(100% - 20px);
      font-family: Kanit;
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
      letter-spacing: 0.1px;
    }

    &.disabled {
      border: 1px solid #dfdcdd;
      background: #f3f2f2;
      cursor: not-allowed;
    }
    &.border-active {
      border-color: #b7b2b5;
    }
    &.warning-border {
      border-color: #eb5554;
    }
  }
}
</style>

<style lang="scss">
// ... (ส่วน style global ไม่ได้เปลี่ยนแปลง)
.dropdownCon {
  overflow-y: auto;
  z-index: 9999;
  padding: 4px;

  .box {
    cursor: pointer;
    padding: 10px;
    border-radius: 4px;

    color: #1f1d1f;
    font-family: Kanit;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0.1px;

    &:hover,
    &.selected {
      background: #fbeff8;
    }
    &.hidden {
      display: none;
    }
  }
}
</style>